<script setup lang="ts">
/**
 * Full-height "All Recents" sheet listing the user's complete recent history
 * across all models, with period chips (all / today / this week / drafts),
 * text search, a model-type filter, per-item removal and clear-all.
 */
import { Link } from '@inertiajs/vue3';
import { BrushCleaning, Search, X } from '@lucide/vue';
import { isThisWeek, isToday } from 'date-fns';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button } from '@/components/ui/button/index';
import { Input } from '@/components/ui/input/index';
import { Kbd } from '@/components/ui/kbd/index';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select/index';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet/index';

import { useRecentHistory } from '../composables/useRecentHistory';
import { useRecentItemDisplay } from '../composables/useRecentItemDisplay';
import type { RecentHistoryItem } from '../types';
import { edit } from '../wayfinder/routes';

const open = defineModel<boolean>('open', { default: false });

const { t } = useI18n();
const { allRecentItems, removeItem, clearHistory } = useRecentHistory();
const { modelIcon, typeLabel, statusDotClass, relativeTime } = useRecentItemDisplay();

type PeriodKey = 'all' | 'today' | 'week' | 'drafts';

const PERIODS: { key: PeriodKey; labelKey: string }[] = [
    { key: 'all', labelKey: 'crud.period_all' },
    { key: 'today', labelKey: 'crud.period_today' },
    { key: 'week', labelKey: 'crud.period_week' },
    { key: 'drafts', labelKey: 'crud.drafts' },
];

const period = ref<PeriodKey>('all');
const searchQuery = ref('');
const typeFilter = ref('all');

/** Distinct model types present in the history, for the type filter dropdown. */
const availableTypes = computed(() => {
    const seen = new Map<string, RecentHistoryItem>();
    for (const item of allRecentItems.value) {
        if (!seen.has(item.model)) seen.set(item.model, item);
    }
    return Array.from(seen.values()).map((item) => ({ model: item.model, label: typeLabel(item) }));
});

function matchesPeriod(item: RecentHistoryItem): boolean {
    switch (period.value) {
        case 'today':
            return isToday(new Date(item.opened_at));
        case 'week':
            return isThisWeek(new Date(item.opened_at), { weekStartsOn: 1 });
        case 'drafts':
            return item.is_draft === true;
        default:
            return true;
    }
}

const filteredItems = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    return allRecentItems.value.filter((item) => {
        if (!matchesPeriod(item)) return false;
        if (typeFilter.value !== 'all' && item.model !== typeFilter.value) return false;
        if (!query) return true;
        return [item.label, item.subtitle, item.badge, item.status]
            .some((value) => value?.toLowerCase().includes(query));
    });
});

const lastAccess = computed(() => {
    const newest = allRecentItems.value[0];
    return newest ? relativeTime(newest.opened_at) : null;
});

function itemHref(item: RecentHistoryItem): string {
    return edit({ model: item.model, id: item.record_id }).url;
}

function clearAll() {
    clearHistory();
    open.value = false;
}
</script>

<template>
    <Sheet v-model:open="open">
        <SheetContent side="right" class="flex w-full flex-col gap-0 p-0 sm:max-w-sm">
            <SheetHeader class="gap-1 border-b p-4">
                <SheetTitle>{{ t('crud.all_recents_title') }}</SheetTitle>
                <SheetDescription>
                    {{ t('crud.items_count', allRecentItems.length) }}
                    <template v-if="lastAccess"> &middot; {{ t('crud.last_access', { time: lastAccess }) }}</template>
                </SheetDescription>
            </SheetHeader>

            <!-- Filters -->
            <div class="space-y-3 border-b p-4">
                <div>
                    <div class="text-muted-foreground pb-2 text-xs font-medium uppercase tracking-wider">
                        {{ t('crud.period') }}
                    </div>
                    <div class="flex flex-wrap gap-1.5">
                        <button
                            v-for="p in PERIODS"
                            :key="p.key"
                            type="button"
                            :class="[
                                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                                period === p.key
                                    ? 'border-primary/50 bg-primary/15 text-primary'
                                    : 'border-border text-muted-foreground hover:text-foreground',
                            ]"
                            @click="period = p.key"
                        >
                            {{ t(p.labelKey) }}
                        </button>
                    </div>
                </div>

                <div class="flex gap-2">
                    <div class="relative flex-1">
                        <Search class="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2" />
                        <Input v-model="searchQuery" type="text" :placeholder="t('crud.search_recent')" class="h-9 pl-8 text-sm" />
                    </div>
                    <Select v-model="typeFilter">
                        <SelectTrigger class="h-9 w-36 shrink-0 text-sm">
                            <SelectValue :placeholder="t('crud.all_types')" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{{ t('crud.all_types') }}</SelectItem>
                            <SelectItem v-for="type in availableTypes" :key="type.model" :value="type.model" class="capitalize">
                                {{ type.label }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <!-- Items -->
            <div class="flex-1 space-y-0.5 overflow-y-auto p-2">
                <div
                    v-for="item in filteredItems"
                    :key="item.id"
                    class="group hover:bg-muted flex items-start gap-2 rounded-lg px-2 py-2"
                >
                    <Link :href="itemHref(item)" class="flex min-w-0 flex-1 items-start gap-3" @click="open = false">
                        <div
                            :class="[
                                'flex size-9 shrink-0 items-center justify-center rounded-lg',
                                item.is_draft ? 'bg-amber-500/15 text-amber-500' : 'bg-muted text-muted-foreground',
                            ]"
                        >
                            <component :is="modelIcon(item)" class="size-4" />
                        </div>
                        <div class="min-w-0 flex-1">
                            <div class="truncate text-sm font-medium">
                                {{ item.is_draft ? t('crud.draft') : item.label }}
                            </div>
                            <div class="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                                <span
                                    :class="[
                                        'inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
                                        item.is_draft ? 'bg-amber-500/15 text-amber-500' : 'bg-muted',
                                    ]"
                                >
                                    <component :is="modelIcon(item)" class="size-2.5" />
                                    {{ item.badge ?? typeLabel(item) }}
                                    <template v-if="item.is_draft"> {{ t('crud.draft') }}</template>
                                </span>
                                <span v-if="item.status" class="flex min-w-0 items-center gap-1.5">
                                    <span :class="['size-1.5 shrink-0 rounded-full', statusDotClass(item)]" />
                                    <span class="truncate">{{ item.status }}</span>
                                </span>
                                <span class="text-muted-foreground/75 ml-auto shrink-0 whitespace-nowrap">
                                    {{ relativeTime(item.opened_at) }}
                                </span>
                            </div>
                        </div>
                    </Link>
                    <Button
                        variant="ghost"
                        size="icon"
                        class="text-muted-foreground size-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                        :aria-label="t('crud.remove')"
                        @click="removeItem(item.model, item.record_id)"
                    >
                        <X class="size-3.5" />
                    </Button>
                </div>

                <div v-if="filteredItems.length === 0" class="text-muted-foreground px-2 py-8 text-center text-sm">
                    {{ t('crud.no_matching_items') }}
                </div>
            </div>

            <!-- Footer -->
            <div class="text-muted-foreground flex items-center justify-between border-t px-4 py-3 text-xs">
                <span class="flex items-center gap-1.5">
                    <Kbd>Esc</Kbd>
                    {{ t('crud.to_close') }}
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    class="text-muted-foreground hover:text-foreground size-7"
                    :title="t('crud.clear_history')"
                    :aria-label="t('crud.clear_history')"
                    @click="clearAll"
                >
                    <BrushCleaning class="size-4" />
                </Button>
            </div>
        </SheetContent>
    </Sheet>
</template>
